// The v2 combined-dashboard panels, ported from Claude Design project
// c733b4d7-8893-43ba-8c64-1ce003b78b83, file "Combined Dashboard v2.dc.html".
// Builds on the existing SectionPanel (panel-head: title/caption + source
// chips + "View X" link) and the shared client-analytics primitives.
//
// Layout composition (side-by-side rows, header, segmented period control)
// is the page-assembler's job — each panel here is a self-contained card.

import type {
  LeadsSection,
  TrafficSection,
  LocalSection,
  ReviewsSection,
  RankingsSection,
  AdsSection,
  CrmSection,
  CrmContactsSection,
  InsightMover,
  EcommerceSection,
  RankBandPoint,
  MultiEngineRankRow,
  HubSpotAttributionSection,
  EmailMarketingSection,
} from "@/types/clientAnalytics";
import type {
  DashboardSearchData,
  DashboardSiteHealthData,
  DashboardContentData,
  DashboardWorkDoneItem,
} from "@/types/dashboardOverview";
import { BarChart3 } from "lucide-react";
import { SectionPanel } from "@/components/dashboard/SectionPanel";
import { TrendChart } from "@/components/dashboard/TrendChart";
import { CallOutcomesDonut } from "@/components/client-analytics/frames/CallOutcomesDonut";
import { LeadLogTable } from "@/components/client-analytics/frames/LeadLogTable";
import {
  RankBandDistributionChart,
  sumLatestRankBand,
} from "@/components/client-analytics/frames/RankBandDistributionChart";
import { MultiEngineRankTable } from "@/components/client-analytics/frames/MultiEngineRankTable";
import { AdEntityBreakdownTable } from "@/components/client-analytics/frames/AdEntityBreakdownTable";
import { TrafficChannelBarChart } from "@/components/client-analytics/frames/TrafficChannelBarChart";
import { LandingPagesTable } from "@/components/client-analytics/frames/LandingPagesTable";
import {
  DealPipelinePanel,
  type DealPipelineStage,
} from "@/components/client-analytics/frames/DealPipelinePanel";
import { InsightsStrip, type Mover } from "@/components/client-analytics/frames/InsightsStrip";
import { EcommerceRevenuePanel } from "@/components/client-analytics/frames/EcommerceRevenuePanel";
import { ReviewsSentimentFrame } from "@/components/client-analytics/frames/ReviewsSentimentFrame";
import { LocalSurfaceFrame } from "@/components/client-analytics/frames/LocalSurfaceFrame";
import { LocalPerLocationFrame } from "@/components/client-analytics/frames/LocalPerLocationFrame";
import { CrmContactsFrame } from "@/components/client-analytics/frames/CrmContactsFrame";
import { DealTable } from "@/components/client-analytics/frames/DealTable";
import { ContactTable } from "@/components/client-analytics/frames/ContactTable";
import { HubSpotAttributionFrame } from "@/components/client-analytics/frames/HubSpotAttributionFrame";
import { EmailMarketingFrame, formatRatePct } from "@/components/client-analytics/frames/EmailMarketingFrame";
// Public dashboard-panel surface for the self-contained LSA frame.
export { LsaPanel } from "@/components/client-analytics/frames/LsaPanel";
import {
  NotConnected,
  Pill,
  BarRow,
  StatBlock,
  KpiStrip,
  HeroKpiTile,
  SourceChip,
  TrendSparkline,
  TrendBadge,
  formatDayTick,
  TrendChartCard,
  ChangePctBadge,
  InsightLine,
  computeTop10BandMovement,
  formatNum,
  formatMoney,
  formatPct,
  formatDurationHMS,
  formatLocalDateRange,
} from "./primitives";

// LabeledCount[] (answered/missed/voicemail/not_set) -> CallOutcomesDonut's
// named-field prop shape. not_set is AA's "no outcome classified yet" bucket
// and maps to the donut's "unclassified" slice.
function mapCallOutcomes(byStatus: { label: string; value: number }[]) {
  const find = (label: string) =>
    byStatus.find((s) => s.label.toLowerCase() === label)?.value ?? 0;
  return {
    answered: find("answered"),
    missed: find("missed"),
    voicemail: find("voicemail"),
    unclassified: find("not_set"),
  };
}

function maxValue(items: { value: number }[]): number {
  return items.reduce((m, i) => Math.max(m, i.value), 0) || 1;
}

// 1. LEADS — hero total + revenue + trend + source breakdown. Adaptive like
// AdvertisingPanel below: not-connected teaser when WhatConverts has never
// been wired up for this client (`data` null/undefined -- Demo client, A+
// Custom Builders), or the real KPI strip even at a genuine zero (map_leads'
// "CONNECTED-BUT-EMPTY vs NEVER-CONNECTED" fix, 2026-08-10 -- a client with a
// working lead tracker that just had no leads this window, e.g. Tyler
// McBroom / ALIBI Music / Konala / The Bunion Cure / Northwest Surgery
// Center / Votacall, gets the honest "0 leads" strip below, never the
// invite). Always mounted unconditionally by the page now, same as
// Advertising/MetaAdsDemographics -- see ClientDashboardPage's comment on
// that row for why an absent section reads dishonestly as "no lead-tracking
// feature" instead of "not connected yet."
export function LeadsPanel({
  data,
  linkTo,
  periodEnd,
}: {
  data?: LeadsSection | null;
  linkTo?: string;
  // Dashboard's period_end (see ClientAnalyticsDashboard) -- lets the badge
  // and sparkline detect a still-in-progress trailing month and drop it
  // instead of plotting/diffing a fabricated cliff. See primitives.tsx
  // trailingBucketIsPartial().
  periodEnd?: string | null;
}) {
  if (!data) {
    return (
      <NotConnected
        message="Leads & Calls — call and form tracking isn't connected for this account"
        cta="Talk to your account manager"
      />
    );
  }
  // Rolling 30-day window (2026-08-11): when the backend could re-sum this
  // client's own DAILY leads series into a genuine rolling 30 days, the hero
  // shows those 30-day figures with a real 30-vs-preceding-30 badge, and the
  // panel caption says so. When it couldn't (monthly/sparse series), the hero
  // stays on the full ~90-day bake totals and the caption says "Last 90 days".
  // Either way the by_source / call-outcomes breakdowns have no daily grain, so
  // they always reflect the ~90-day bake and are labelled with it — never a
  // 30-day figure they can't back (this is the mixed-window lie we must avoid).
  const rolling = data.window ?? null;
  const isRolling = rolling != null;
  const heroLeads = isRolling ? rolling.values.total_leads ?? data.total_leads : data.total_leads;
  const heroCalls = isRolling ? rolling.values.phone_calls ?? data.phone_calls : data.phone_calls;
  const heroForms = isRolling ? rolling.values.web_forms ?? data.web_forms : data.web_forms;
  const leadsCaption = isRolling
    ? "Last 30 days · call & form tracking"
    : "Last 90 days · call & form tracking";
  // Breakdown cards below the hero are ~90-day bake data; when the hero is a
  // 30-day figure, spell that out so the two windows are never confused.
  const bakeWindowLabel = isRolling ? "Last 90 days" : "";

  const srcMax = maxValue(data.by_source.map((s) => ({ value: s.value })));
  const hasCallOutcomes = data.by_status && data.by_status.length > 0;
  const callOutcomes = hasCallOutcomes ? mapCallOutcomes(data.by_status) : null;
  // Headline is the business story: volume · quality · how much came unpaid.
  const hasQualified = typeof data.qualified === "number" && data.qualified > 0;
  const unclassified = typeof data.unclassified === "number" ? data.unclassified : 0;
  const unpaidPct =
    typeof data.unpaid_share === "number" ? Math.round(data.unpaid_share * 100) : null;
  // Revenue is opt-in data (most clients never wire it up) — only speak when
  // there's a real number to report, never a fabricated $0.
  const revenueParts: string[] = [];
  if (data.quote_value && data.quote_value > 0) {
    revenueParts.push(`${formatMoney(data.quote_value, data.currency)} quoted`);
  }
  if (data.sales_value && data.sales_value > 0) {
    revenueParts.push(`${formatMoney(data.sales_value, data.currency)} won`);
  }
  return (
    <SectionPanel
      title="Leads & Calls"
      caption={leadsCaption}
      action={<SourceChip label="Call & form tracking" />}
      link={linkTo ? { label: "View lead log", to: linkTo } : undefined}
    >
      <div className="flex gap-5 items-stretch p-5">
        <div className="flex-[1.3] flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Total leads
            </div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="text-[44px] leading-[44px] font-bold tabular">{formatNum(heroLeads)}</div>
              {/* 30-vs-preceding-30 when the hero is a real rolling-30 figure;
                  otherwise the daily-trend badge as before. */}
              {isRolling ? (
                <ChangePctBadge pct={rolling.changes.total_leads} />
              ) : (
                <TrendBadge points={data.over_time} periodEnd={periodEnd} />
              )}
              {/* qualified / unpaid-share are ~90-day derived metrics with no
                  daily grain — shown inline only in 90-day mode; in 30-day mode
                  they move to the explicitly-labelled bake line below so they're
                  never misread as 30-day. */}
              {!isRolling && hasQualified && <Pill tone="up">{formatNum(data.qualified)} qualified</Pill>}
              {!isRolling && unpaidPct !== null && (
                <span className="text-dash-subtitle text-muted-foreground">{unpaidPct}% unpaid</span>
              )}
            </div>
            <div className="text-dash-context text-muted-foreground">
              {formatNum(heroCalls)} calls · {formatNum(heroForms)} forms
              {!isRolling && hasQualified && typeof data.qualified_rate === "number"
                ? ` · ${Math.round(data.qualified_rate * 100)}% qualified`
                : ""}
            </div>
            {!isRolling && revenueParts.length > 0 && (
              <div className="text-dash-context text-foreground font-medium">
                {revenueParts.join(" · ")}
              </div>
            )}
            {!isRolling && !hasQualified && unclassified > 0 && (
              <div className="text-dash-context text-[hsl(var(--status-warning))]">
                {formatNum(unclassified)} leads not yet classified — qualification pending
              </div>
            )}
            {/* In 30-day mode, the ~90-day derived/revenue figures still have
                value — surfaced here on their own honest window rather than
                dropped, and never stacked next to the 30-day hero as if they
                shared its window. */}
            {isRolling &&
              (hasQualified || unpaidPct !== null || revenueParts.length > 0) && (
                <div className="text-dash-context text-muted-foreground">
                  Last 90 days:{" "}
                  {[
                    hasQualified ? `${formatNum(data.qualified)} qualified` : null,
                    unpaidPct !== null ? `${unpaidPct}% unpaid` : null,
                    ...revenueParts,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}
          </div>
          <div className="flex-1 min-h-[90px] rounded-lg border border-border bg-card p-3.5 flex flex-col gap-1.5">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Leads trend{bakeWindowLabel && ` · ${bakeWindowLabel.toLowerCase()}`}
            </div>
            <TrendSparkline points={data.over_time} height={80} periodEnd={periodEnd} />
          </div>
        </div>
        <div className={hasCallOutcomes ? "flex-[1.7]" : "flex-[2]"} style={{ minWidth: 0 }}>
          <div className="h-full rounded-lg border border-border bg-card p-4 flex flex-col gap-2.5">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Where leads came from{bakeWindowLabel && ` · ${bakeWindowLabel.toLowerCase()}`}
            </div>
            {data.by_source.map((s) => (
              <BarRow
                key={s.label}
                label={s.label}
                value={formatNum(s.value)}
                pct={Math.round((s.value / srcMax) * 100)}
              />
            ))}
          </div>
        </div>
        {hasCallOutcomes && callOutcomes && (
          <div className="flex-[1.4] rounded-lg border border-border bg-card p-4 flex flex-col gap-2.5">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Call outcomes{bakeWindowLabel && ` · ${bakeWindowLabel.toLowerCase()}`}
            </div>
            <CallOutcomesDonut {...callOutcomes} />
          </div>
        )}
      </div>
      {data.recent_leads && data.recent_leads.length > 0 && (
        <div className="px-5 pb-5">
          {/* Lead log is ~90-day bake detail; when the hero is a 30-day figure,
              label the log's own window so it's never read as the 30-day set. */}
          <LeadLogTable
            rows={data.recent_leads}
            totalLeads={data.total_leads}
            windowLabel={bakeWindowLabel ? bakeWindowLabel.toLowerCase() : null}
          />
        </div>
      )}
    </SectionPanel>
  );
}

// AA's "Google Change" tile renders the WHOLE value (arrow + number) in one
// coloured unit -- e.g. a solid green "▲ 44" -- not a neutral number with a
// small separate badge like the other KPI tiles in this panel. Direction-
// of-change colour, so status tokens (never coral/teal), matching RankCell's
// own ↑/↓ badge convention below in MultiEngineRankTable.
function GoogleChangeValue({ value }: { value: number | null }) {
  if (value === null) return <>—</>;
  const positive = value >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 ${
        positive ? "text-[hsl(var(--status-success))]" : "text-[hsl(var(--status-error))]"
      }`}
    >
      <span aria-hidden="true">{positive ? "▲" : "▼"}</span>
      {Math.abs(value)}
    </span>
  );
}

// 2. SEARCH PERFORMANCE — the dominant band: GSC KPIs + clicks trend + top keywords.
export function SearchPerformancePanel({
  search,
  rankings,
  linkTo = "#",
  rankDistribution,
  multiEngineRanks,
  multiEngineRanksTotal,
  periodEnd,
}: {
  search: DashboardSearchData | null;
  /**
   * `null` means the rank-tracker MCP pull has never run for this client
   * (a genuinely not-connected tracker) -- distinct from a connected
   * tracker that happens to report `tracked: 0`. `ClientDashboardPage`
   * passes `data.rankings` straight through now; it used to collapse both
   * cases to a shared `EMPTY_RANKINGS` object before this ever reached the
   * panel, which is what made the "Top ranking keywords" fallback below
   * read as "connected, zero results" for a client that was never
   * connected at all (architect-flagged honesty gap, 2026-08-10 —
   * All About Plumbing). Callers: keep passing the real nullable value,
   * don't reintroduce a shared empty-object fallback at the call site.
   */
  rankings: RankingsSection | null;
  linkTo?: string;
  /** AA REST-key "campaign-rankings" — rank-band mix over time, native SEO only. */
  rankDistribution?: RankBandPoint[] | null;
  /** AA REST-key "keyword-rankings" — per-keyword rank across engines/surfaces. */
  multiEngineRanks?: MultiEngineRankRow[] | null;
  /**
   * Pre-cap row count (`MultiEngineRankSection.total_rows`) for the table's
   * "Showing N of Total Rows" caption — landed on the live schema
   * 2026-08-09 (commit 78fc402c6), confirmed exact against ALIBI Music (50
   * of 70). Optional and defaults to `rows.length` only for a snapshot
   * built before that commit, so this never regresses to a fabricated
   * total on an older payload.
   */
  multiEngineRanksTotal?: number | null;
  /**
   * Dashboard's period_end (see ClientAnalyticsDashboard) — same prop
   * LeadsPanel/TrafficPanel already take. Its only job here is the
   * "Google Change" tile's "as of" disclosure (see GoogleChangeValue):
   * `RankingsSection` refreshes only via the manual CLI backfill
   * (`aa_backfill.py --platform rank-tracker`), never the nightly Feed-A
   * job, so a live-looking "▲N" with no cue would misrepresent a
   * possibly-stale number as current (architect ruling, 2026-08-09). No
   * per-field pull timestamp exists in the schema to disclose precisely
   * (checked `RankingsSection` — no date field at all), so this anchors
   * to the dashboard's own reporting window end instead: a real date
   * already flowing through the page, not a fabricated pull time.
   */
  periodEnd?: string | null;
}) {
  const hasRankDistribution = !!rankDistribution && rankDistribution.length > 0;
  const hasMultiEngineRanks = !!multiEngineRanks && multiEngineRanks.length > 0;
  // AA recommendation-block pattern (P3-3): deterministic, threshold-derived
  // one-liner from the same rank_distribution series the chart below plots
  // -- null (no line) when there aren't two periods to compare or nothing
  // moved, never a fabricated observation.
  const rankMovementInsight = computeTop10BandMovement(rankDistribution);
  // AA "Rank Tracker - Keywords" big-number tiles, both confirmed live
  // against ALIBI Music (rank-backend, 2026-08-09) -- see that commit's
  // message for the exact values measured at the time, not repeated here:
  // a point-in-time number in a comment goes stale the moment the next
  // snapshot rebuild lands (it already has, twice, on this exact pair of
  // tiles), so this comment describes the FORMULA, not a measurement.
  // googleRankingsValue is derived client-side from the same series the
  // chart plots (see sumLatestRankBand's doc comment); googleChangeValue
  // comes straight off the schema field rank-backend added for exactly
  // this tile. Each is independently null-safe -- a client with
  // rank_distribution but a stale/unrun rankings MCP backfill still gets a
  // real "Google Rankings" tile and a "—" for "Google Change". `rankings`
  // itself is nullable (not-connected tracker) -- optional-chained here,
  // same as everywhere else `rankings` is read in this component.
  const googleRankingsValue = sumLatestRankBand(rankDistribution);
  const googleChangeValue = rankings?.google_ranking_change_total ?? null;
  const hasRankTrackerTiles = googleRankingsValue !== null || googleChangeValue !== null;
  // "As of" disclosure for the Google Change tile only -- see the
  // `periodEnd` prop doc comment above for why this exists and why it's
  // the dashboard's period_end rather than a precise pull timestamp.
  // Omitted (no caption) whenever there's no real value to caveat in the
  // first place, matching this file's "never a fabricated cue" pattern.
  const googleChangeAsOf =
    googleChangeValue !== null && periodEnd ? `as of ${formatDayTick(periodEnd)}` : undefined;

  if (!search) {
    return (
      <SectionPanel
        title="Search Performance"
        caption="Search Console isn't connected yet"
        surfaceEmphasis="primary"
        action={<SourceChip label="Rank tracker" />}
        link={{ label: "View keywords", to: linkTo }}
      >
        <div className="p-5">
          <NotConnected message="Connect Search Console to see clicks, impressions, and position here." />
        </div>
        {hasRankDistribution && (
          <div className="border-t border-border p-5 flex flex-col gap-3">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Rank distribution over time
            </div>
            <InsightLine text={rankMovementInsight} />
            <RankBandDistributionChart data={rankDistribution!} />
          </div>
        )}
        {hasRankTrackerTiles && (
          <div className="border-t border-border p-5">
            <KpiStrip>
              <StatBlock
                label="Google Rankings"
                value={googleRankingsValue !== null ? formatNum(googleRankingsValue) : "—"}
              />
              <StatBlock
                label="Google Change"
                value={<GoogleChangeValue value={googleChangeValue} />}
                context={googleChangeAsOf}
              />
            </KpiStrip>
          </div>
        )}
        {hasMultiEngineRanks && (
          <div className="border-t border-border p-5">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground pb-2">
              Keyword rankings by engine
            </div>
            <MultiEngineRankTable rows={multiEngineRanks!} totalCount={multiEngineRanksTotal ?? undefined} />
          </div>
        )}
      </SectionPanel>
    );
  }

  const trendData = search.clicks_trend.map((v, i) => ({ day: i, clicks: v }));
  // Google position: lower is better, so a NEGATIVE avg_position_delta is an
  // improvement (green) — opposite sign convention from clicks/impressions.
  const positionImproved = (search.avg_position_delta ?? 0) < 0;
  // Caption the GSC block from the window the endpoint ACTUALLY returned
  // (search.period_start/period_end), never a hardcoded "Last 90 days": the
  // picker is gone and the client dashboard requests a fixed GSC window, so the
  // caption must state the real fetched range. No range claimed when the
  // bounds are absent (never a fabricated one). The rankings table below keeps
  // its own ~90-day rank-tracker context, a separate data source.
  const gscRange = formatLocalDateRange(search.period_start, search.period_end);
  const searchCaption = gscRange
    ? `${gscRange} · Search Console lags ~3 days`
    : "Search Console · lags ~3 days";

  return (
    <SectionPanel
      title="Search Performance"
      caption={searchCaption}
      surfaceEmphasis="primary"
      action={
        <div className="flex items-center gap-2">
          <SourceChip label="Search Console" />
          <SourceChip label="Rank tracker" />
        </div>
      }
      link={{ label: "View keywords", to: linkTo }}
    >
      <div className="flex gap-5 items-stretch p-5">
        <div className="flex-[5] flex flex-col gap-3.5 justify-center">
          <div className="flex flex-col gap-1.5">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Clicks from Google
            </div>
            <div className="flex items-center gap-3">
              <span className="text-dash-page font-bold tabular">{formatNum(search.clicks)}</span>
              <Pill tone={search.clicks_delta_pct >= 0 ? "up" : "down"}>
                {search.clicks_delta_pct >= 0 ? "↑" : "↓"} {Math.abs(Math.round(search.clicks_delta_pct))}%
              </Pill>
            </div>
            <div className="text-dash-context text-muted-foreground">vs prior period</div>
          </div>
          <div className="flex gap-2.5">
            <div className="flex-1 rounded-lg border border-border bg-card p-3.5 flex flex-col gap-1">
              <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Impressions
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-dash-value font-bold tabular">{formatNum(search.impressions)}</div>
                <Pill tone={search.impressions_delta_pct >= 0 ? "up" : "down"}>
                  {search.impressions_delta_pct >= 0 ? "↑" : "↓"}{" "}
                  {Math.abs(Math.round(search.impressions_delta_pct))}%
                </Pill>
              </div>
            </div>
            <div className="flex-1 rounded-lg border border-border bg-card p-3.5 flex flex-col gap-1">
              <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Avg position
              </div>
              <div className="flex items-center gap-2">
                <div className="text-dash-value font-bold tabular">
                  {search.avg_position != null ? search.avg_position.toFixed(1) : "—"}
                </div>
                {search.avg_position_delta ? (
                  <Pill tone={positionImproved ? "up" : "down"}>
                    {positionImproved ? "↑" : "↓"} {Math.abs(search.avg_position_delta).toFixed(1)}
                  </Pill>
                ) : null}
              </div>
            </div>
            <div className="flex-1 rounded-lg border border-border bg-card p-3.5 flex flex-col gap-1">
              <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Pages indexed
              </div>
              <div className="text-dash-value font-bold tabular">{formatNum(search.pages_indexed)}</div>
            </div>
          </div>
        </div>
        <div className="flex-[7] rounded-lg border border-border bg-card p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Clicks trend
            </div>
            <div className="text-dash-context text-muted-foreground">daily</div>
          </div>
          {trendData.length > 1 ? (
            <TrendChart
              data={trendData}
              series={[{ dataKey: "clicks", name: "Clicks" }]}
              xKey="day"
              height={120}
              xTickFormatter={() => ""}
              showCurrentValue={false}
            />
          ) : (
            <div className="h-[120px] flex items-center justify-center text-dash-context text-muted-foreground">
              Not enough data yet
            </div>
          )}
        </div>
      </div>
      {/* Band-distribution trend is the headline SEO visual — it leads,
          ahead of any keyword table (architect layout ruling, P2-4). */}
      {hasRankDistribution && (
        <div className="border-t border-border p-5 flex flex-col gap-3">
          <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
            Rank distribution over time
          </div>
          <InsightLine text={rankMovementInsight} />
          <RankBandDistributionChart data={rankDistribution!} />
        </div>
      )}
      {hasRankTrackerTiles && (
        <div className="border-t border-border p-5">
          <KpiStrip>
            <StatBlock
              label="Google Rankings"
              value={googleRankingsValue !== null ? formatNum(googleRankingsValue) : "—"}
            />
            <StatBlock
              label="Google Change"
              value={<GoogleChangeValue value={googleChangeValue} />}
              context={googleChangeAsOf}
            />
          </KpiStrip>
        </div>
      )}
      {/* Exactly one keyword table renders, never both: the multi-engine
          table (rank + change + volume + 3 more engines) strictly
          dominates the flat table's near-identical rows, so it REPLACES
          rather than supplements it. The flat table is the fallback for
          clients without REST-key data (8/40 as of this task). */}
      {hasMultiEngineRanks ? (
        <div className="border-t border-border p-5">
          <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground pb-2">
            Keyword rankings by engine
          </div>
          <MultiEngineRankTable rows={multiEngineRanks!} totalCount={multiEngineRanksTotal ?? undefined} />
        </div>
      ) : rankings ? (
        <div className="border-t border-border p-5">
          <div className="flex items-center justify-between pb-2">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Top ranking keywords
            </div>
            <div className="text-dash-context text-muted-foreground">
              {formatNum(rankings.tracked)} tracked · {formatNum(rankings.top_3)} in top 3 ·{" "}
              {formatNum(rankings.top_10)} in top 10 · rank tracker
            </div>
          </div>
          <div className="flex text-dash-label font-semibold uppercase tracking-wide text-muted-foreground border-b border-border pb-2">
            <div className="flex-[2.8]">Keyword</div>
            <div className="flex-1 text-right">Searches / mo</div>
            <div className="flex-1 text-center">Position</div>
            <div className="flex-[0.8] text-right">Change</div>
          </div>
          {rankings.keywords.map((k) => {
            const top = (k.google_ranking ?? 99) <= 3;
            const up = (k.google_ranking_change ?? 0) > 0;
            return (
              <div key={k.keyword} className="flex items-center border-b border-border/50 py-2.5">
                <div className="text-dash-subtitle flex-[2.8]">{k.keyword}</div>
                <div className="text-dash-subtitle tabular text-muted-foreground flex-1 text-right">
                  {formatNum(k.volume)}
                </div>
                <div className="flex-1 text-center">
                  <span
                    className={
                      "inline-flex items-center justify-center min-w-[30px] h-[30px] rounded-md font-bold tabular text-dash-subtitle " +
                      (top
                        ? "bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]"
                        : "bg-coral-light text-coral-dark")
                    }
                  >
                    {k.google_ranking ?? "—"}
                  </span>
                </div>
                <div className="text-dash-subtitle tabular flex-[0.8] text-right">
                  <Pill tone={up ? "up" : "neutral"}>
                    {k.google_ranking_change
                      ? up
                        ? `+${k.google_ranking_change}`
                        : k.google_ranking_change
                      : "steady"}
                  </Pill>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // `rankings === null` -- the rank-tracker MCP pull has never run for
        // this client (e.g. All About Plumbing, no REST-key data either).
        // Rendering the same header off a shared EMPTY_RANKINGS-shaped
        // default used to show "0 tracked · 0 in top 3 · 0 in top 10 · rank
        // tracker" here -- reading as a connected tracker with no results,
        // not as "no tracker at all" (architect-flagged honesty gap,
        // 2026-08-10). Explicit not-connected state instead, same pattern
        // the `!search` branch above already uses for Search Console.
        <div className="border-t border-border p-5">
          <NotConnected message="Connect a rank tracker to see keyword position data here." />
        </div>
      )}
    </SectionPanel>
  );
}

// 3. TRAFFIC — GA4 band: hero KPI-sparkline tiles + sessions/Previous Period
// trend + channel breakdown + landing pages. Rebuilt to AA's own "Google
// Analytics" report page (reference screenshot, 2026-08-09; ALIBI Music,
// Jul 2026 vs Jun 2026) rather than the prior condensed dashboard-band
// version — same evolution MetaAdsPanel went through (a KPI strip growing
// into a full per-entity report), not a new pattern for this codebase.
//
// KPI-tile-sparkline ruling conflict — see HeroKpiTile's own doc comment in
// primitives.tsx for the full reasoning: an earlier ruling reserved the
// sparkline-in-a-tile pattern for exactly ONE hero tile system-wide (ad
// spend); this band ships THREE (Total Sessions / Organic Sessions /
// Average Session Duration) because that's what AA's own reference report
// does. Flagged for the team-lead to route, not silently decided either way
// here — see the review request.
export function TrafficPanel({
  data,
  linkTo = "#",
  periodEnd,
}: {
  data: TrafficSection;
  linkTo?: string;
  // See LeadsPanel's periodEnd doc -- same partial-trailing-bucket guard.
  periodEnd?: string | null;
}) {
  return (
    <SectionPanel
      title="Website Traffic"
      caption="Last 90 days · Google Analytics"
      action={<SourceChip label="Google Analytics" />}
      link={{ label: "View traffic", to: linkTo }}
    >
      <div className="flex flex-col gap-5 p-5">
        <div className="flex gap-5">
          <HeroKpiTile
            label="Total Sessions"
            icon={<BarChart3 className="h-3.5 w-3.5" />}
            value={formatNum(data.sessions)}
            badge={<ChangePctBadge pct={data.comparison?.sessions_change_pct} />}
            sparkline={data.sessions_over_time}
            periodEnd={periodEnd}
          />
          <HeroKpiTile
            label="Organic Sessions"
            icon={<BarChart3 className="h-3.5 w-3.5" />}
            value={formatNum(data.organic_sessions)}
            badge={<ChangePctBadge pct={data.comparison?.organic_sessions_change_pct} />}
            sparkline={data.organic_sessions_over_time}
            periodEnd={periodEnd}
          />
          <HeroKpiTile
            label="Average Session Duration"
            icon={<BarChart3 className="h-3.5 w-3.5" />}
            value={formatDurationHMS(data.avg_session_duration_seconds)}
            badge={<ChangePctBadge pct={data.comparison?.avg_session_duration_change_pct} />}
            sparkline={data.avg_session_duration_over_time}
            periodEnd={periodEnd}
          />
        </div>
        {/* flex-wrap (task #24-follow-up, same KPI-row-clipping class of bug
            confirmed live on AdvertisingPanel's tile row below -- see that
            row's own comment for the full mechanism) -- StatBlock's default
            min-width:auto already protects each tile's readable content
            width; this just lets the row spill onto a second line instead
            of silently overflowing the page's overflow-x-hidden main
            column once 4 tiles' natural widths exceed the container. */}
        <div className="flex-1 flex flex-wrap rounded-lg border border-border bg-card divide-x divide-border">
          <StatBlock label="Visitors" value={formatNum(data.total_users)} />
          {data.new_users > 0 && <StatBlock label="New visitors" value={formatNum(data.new_users)} />}
          {data.engagement_rate != null && <StatBlock label="Engaged" value={formatPct(data.engagement_rate)} />}
          {/* GA4's own "conversions"/"key events" total (TrafficSection.conversions) --
              renamed here to "Key Events" per GA4's own current terminology (see
              LandingPageRow.key_events' docstring). Previously mislabeled "Form fills",
              which is a DIFFERENT, WhatConverts-sourced metric (LeadsSection.web_forms,
              rendered on LeadsPanel) -- a client with GA4 key events unconfigured shows
              0 here while genuinely having real web-form leads elsewhere on the same
              page (task #23, live-confirmed: Alamo City Urgent Care shows 0 GA4 key
              events but 425 real WhatConverts web-form leads), and a client with both
              wired shows two DIFFERENT numbers under the same claimed label (AAA Fence:
              244 GA4 key events vs. 203 WhatConverts web forms) -- two numbers for one
              metric on one page is worse than either being off, so this tile now names
              what it actually is instead of colliding with LeadsPanel's real form-fill
              count. */}
          {data.conversions != null && <StatBlock label="Key Events" value={formatNum(data.conversions)} />}
        </div>
        {/* min-w-0 on both columns: `flex-1` alone leaves the flexbox default
            `min-width: auto` content floor in place, so the wider column
            refuses to shrink to its half and pushes its sibling off the
            container's right edge -- the same clipping this file's tile rows
            already wrap to avoid, one level up at the row itself. */}
        <div className="flex gap-5 items-stretch">
          <div className="flex-1 min-w-0">
            {/* prior_value on each sessions_over_time point drives the
                paler "Previous Period" overlay automatically — see
                TrendChartCard's own doc comment. */}
            <TrendChartCard
              title="Sessions"
              value={formatNum(data.sessions)}
              badge={<ChangePctBadge pct={data.comparison?.sessions_change_pct} />}
              seriesName="Sessions"
              points={data.sessions_over_time}
              height={260}
            />
          </div>
          <div className="flex-1 min-w-0 rounded-lg border border-border bg-card p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Sessions by Channel
              </div>
              <div className="flex items-center gap-2">
                <span className="text-dash-value font-bold tabular">{formatNum(data.sessions)}</span>
                <ChangePctBadge pct={data.comparison?.sessions_change_pct} />
              </div>
            </div>
            <TrafficChannelBarChart data={data.by_channel} height={220} />
          </div>
        </div>
        {data.top_landing_pages.length > 0 && (
          <LandingPagesTable
            rows={data.top_landing_pages}
            totalCount={data.total_landing_pages}
            capped={data.landing_pages_capped}
          />
        )}
      </div>
    </SectionPanel>
  );
}

// 4. LOCAL PRESENCE — GBP KPIs + reviews inset + profile views trend.
export function LocalPanel({
  local,
  reviews,
  linkTo,
  periodEnd,
}: {
  local: LocalSection;
  reviews?: ReviewsSection | null;
  linkTo?: string;
  // See LeadsPanel's periodEnd doc -- same partial-trailing-bucket guard.
  periodEnd?: string | null;
}) {
  const rating = reviews?.average_rating ?? local.average_rating ?? 0;
  const reviewCount = reviews?.total_reviews ?? local.reviews ?? 0;
  const stars = "★".repeat(Math.round(rating)).padEnd(5, "☆");
  // Sentiment/by-source breakdown only exists for clients on a reputation
  // platform beyond base GMB (BirdEye/GradeUs) — hidden until then; base
  // rating/total above already covers the GMB-only case, so this frame adds
  // nothing (and isn't rendered) when only those two fields are set.
  const hasReviewsSentimentDetail = !!reviews?.sentiment || !!(reviews?.by_source && reviews.by_source.length > 0);
  const hasSurfaceBreakdown = !!local.by_surface && local.by_surface.length > 0;
  // 11 of 29 GBP-connected clients are multi-location (Alamo City Urgent
  // Care, Prestige ER, Edelman-Lyon, All About Plumbing, Konala, All About
  // Heat and Air, JT McDermott Remodeling, KJ Electrical, Lanracorp, STL
  // Dent Co, Gurr Real Estate); the other 18 GBP clients are genuinely
  // single-location and locations comes back empty -- render nothing for
  // them rather than a redundant one-card grid duplicating by_surface above.
  const hasPerLocationBreakdown = !!local.locations && local.locations.length > 0;
  // interactions_over_time is optional (undefined until the gbp-be lane
  // lands it) -- gate on it existing at all rather than on >=2 points so a
  // brand-new pull with 1 point still gets TrendChartCard's own "not enough
  // data yet" placeholder instead of the whole section vanishing, but a
  // client this frontend just hasn't been backed for yet shows nothing.
  const hasInteractionsData = !!local.interactions_over_time && local.interactions_over_time.length > 0;
  return (
    <SectionPanel
      title="Local Presence"
      caption="Last 90 days · Google Business Profile"
      action={<SourceChip label="Google Business Profile" />}
      link={linkTo ? { label: "View local", to: linkTo } : undefined}
    >
      <div className="flex gap-5 items-stretch p-5">
        {/* Fixed 2x2 (task #24-second-follow-up), not flex-wrap -- confirmed
            live on Alamo City Urgent Care at 1519px: this strip only shares
            2.4/3.4 of LocalPanel's content width with the Average Rating card
            next to it (676px), the narrowest KPI-row allocation on the
            dashboard for a four-tile row. flex-wrap's natural reflow fits 3
            tiles (Profile views/Calls/Website visits) on line 1 and strands
            "Directions" alone on line 2, where StatBlock's flex-1
            (flex-basis:0 -- see its own comment) has no line-mate to share
            growth with and stretches the tile to the full 676px even though
            its content only needs ~225px: a lone oversized tile beside
            Advertising's tightly-packed wrap below it, not broken but
            visibly unfinished on a client-facing dashboard. At 1280px the
            same flex-wrap row happens to land a clean 2x2 purely because the
            narrower container only fits 2 tiles per line -- this keeps that
            exact pairing (Profile views+Calls, then Website visits+
            Directions, same DOM order as today) but makes it unconditional
            instead of leaving it to whichever width happens to fit 2 vs. 3
            tiles per line. Two nested divide-x rows stacked with divide-y on
            the outer wrapper, not a single 4-item CSS grid -- Tailwind's
            divide-x/divide-y both key off the same `> * + *` sibling
            selector, so combining them on one flat grid would also stamp a
            stray left border onto the first tile of row 2 (Website visits);
            nesting scopes each divider rule to the row it actually belongs
            to. Every pair below only has to share half of 676px (~338px),
            comfortably inside any 2 of these 4 tiles' combined min-content
            width (the previous 3-tiles-fit-676px measurement already proves
            the four values here are nowhere near that wide -- unlike the
            money-figure tiles elsewhere on this dashboard), so this can't
            reintroduce the clipping 5bb43089a fixed. */}
        <div className="flex-[2.4] flex flex-col rounded-lg border border-border bg-card divide-y divide-border">
          <div className="flex divide-x divide-border">
            <StatBlock
              label="Profile views"
              value={formatNum(local.impressions)}
              context="search + maps"
              // Real period-over-period delta (local.comparison), not TrendBadge
              // off the raw daily impressions_over_time -- same fix as
              // AdvertisingPanel's cost/impressions/clicks badges below: a
              // TrendBadge diffs a series' last two points, which reads as
              // day-over-day now that this series is daily, not the "Business
              // Profile Impressions ▲17%" period comparison AA's own report
              // shows.
              badge={<ChangePctBadge pct={local.comparison?.impressions_change_pct} />}
            />
            <StatBlock
              label="Calls"
              value={formatNum(local.call_clicks)}
              context="tap-to-call"
              badge={<ChangePctBadge pct={local.comparison?.call_clicks_change_pct} />}
            />
          </div>
          <div className="flex divide-x divide-border">
            <StatBlock
              label="Website visits"
              value={formatNum(local.website_clicks)}
              context="from profile"
              badge={<ChangePctBadge pct={local.comparison?.website_clicks_change_pct} />}
            />
            <StatBlock
              label="Directions"
              value={formatNum(local.direction_requests)}
              context="requests"
              badge={<ChangePctBadge pct={local.comparison?.direction_requests_change_pct} />}
            />
          </div>
        </div>
        <div className="flex-1 rounded-lg border border-border bg-card p-4 flex flex-col gap-1.5 justify-center">
          {/* AA's "All Locations - Average Rating" tile: with no
              per-physical-location split available (see LocalSection.by_surface's
              doc comment), local.average_rating already IS the all-locations
              average -- same number, this card just also carries the review
              count context AA splits into a separate place. */}
          <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
            All Locations · Average Rating
          </div>
          <div className="flex items-center gap-2">
            <span className="text-dash-value font-bold tabular">{rating ? rating.toFixed(1) : "—"}</span>
            <span className="text-[hsl(var(--status-warning))] text-base tracking-widest">{stars}</span>
            <ChangePctBadge pct={local.comparison?.average_rating_change_pct} />
          </div>
          <div className="text-dash-context text-muted-foreground">{formatNum(reviewCount)} new this period</div>
        </div>
      </div>
      <div className="border-t border-border p-5">
        {/* Was TrendSparkline (height=90, blanked X ticks, no Y-axis labels)
            -- confirmed live on both Alamo City and AAA Fence: a real ~90-day
            DAILY series (genuinely volatile day to day, not flat -- the KPI
            above is populated and non-trivial) rendered as a flat filled
            block instead of a legible line. TrendSparkline is the compact
            "mini chart in a KPI tile" component (see its own doc comment:
            reserved for tight strips like Leads/Traffic KPI rows); this is
            a dedicated full-width section with its own header, the same
            slot "Interactions" immediately below it already uses
            TrendChartCard for -- real day-tick X-axis, a real labeled
            Y-axis, and 220px of height instead of 90px give this volatile a
            daily series room to actually show its shape. `impressions_over_time`
            also carries `prior_value` (AA's day-offset-matched prior-period
            pull) when the backend ships it, so this picks up the same
            "Previous Period" overlay Interactions/Meta Ads Clicks/Traffic
            already render, for free. */}
        <TrendChartCard
          title="Profile views trend"
          value={formatNum(local.impressions)}
          badge={<ChangePctBadge pct={local.comparison?.impressions_change_pct} />}
          seriesName="Profile views"
          points={local.impressions_over_time}
        />
      </div>
      {hasInteractionsData && (
        <div className="border-t border-border p-5">
          {/* AA plots Business Profile Impressions/Interactions as daily BAR
              charts on this report page. We use the same area-with-"Previous
              Period"-overlay TrendChartCard every other daily trend on this
              dashboard uses (Meta Ads Clicks, Traffic, Leads) instead of
              introducing a one-off bar chart here -- a viewer reading down
              this single dashboard should see "daily trend" as one visual
              language throughout, not have Local switch marks on them
              mid-page. AA's own report mixes bars and lines across ITS
              different sections too, so matching AA metric-by-metric isn't
              actually "the AA convention" to begin with; internal
              consistency wins the tie. */}
          <TrendChartCard
            title="Interactions"
            value={formatNum(local.interactions)}
            badge={<ChangePctBadge pct={local.comparison?.interactions_change_pct} />}
            seriesName="Interactions"
            points={local.interactions_over_time}
          />
        </div>
      )}
      {hasSurfaceBreakdown && (
        <div className="border-t border-border p-5">
          <LocalSurfaceFrame by_surface={local.by_surface} />
        </div>
      )}
      {hasPerLocationBreakdown && (
        <div className="border-t border-border p-5">
          <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground pb-2">
            By location
          </div>
          <LocalPerLocationFrame locations={local.locations} />
        </div>
      )}
      {hasReviewsSentimentDetail && (
        <div className="border-t border-border p-5">
          <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground pb-2">
            Review sentiment
          </div>
          <ReviewsSentimentFrame
            total_reviews={reviews?.total_reviews ?? reviewCount}
            average_rating={reviews?.average_rating ?? undefined}
            sentiment={reviews?.sentiment ?? undefined}
            by_source={reviews?.by_source && reviews.by_source.length > 0 ? reviews.by_source : undefined}
          />
        </div>
      )}
    </SectionPanel>
  );
}

// 5. ADVERTISING — adaptive: not-connected teaser, or KPI strip when running.
export function AdvertisingPanel({
  data,
  periodEnd,
}: {
  data?: AdsSection | null;
  // See LeadsPanel's periodEnd doc -- same partial-trailing-bucket guard.
  periodEnd?: string | null;
}) {
  if (!data) {
    return (
      <NotConnected
        message="Advertising — Google Ads isn't running for this account"
        cta="Talk to your account manager"
      />
    );
  }
  return (
    <SectionPanel
      title="Advertising"
      caption="Last 90 days · Google Ads"
      action={<SourceChip label="Google Ads" />}
    >
      <div className="p-5 flex flex-col gap-5">
        {/* THE confirmed live defect (2026-08-08 browser walk, Alamo City
            Urgent Care): 7 tiles in a `nowrap` flex-1 row -- StatBlock's
            default `min-width: auto` already refuses to shrink each tile
            below its own content's min-content width (a comma-grouped money
            value and a percent-badge pair can't break onto a second line),
            so the row's natural width routinely exceeds its container at
            realistic viewport widths (measured live: 1772px of content in a
            1262px box at a 1519px viewport). With no `flex-wrap` and no
            horizontal-scroll affordance on this div, and the page's own
            main content column set to `overflow-x-hidden` with no scrollbar
            (ModernMainLayout.tsx), the last 2-3 tiles -- Conversions, Return
            on Spend -- simply vanished past the right edge with zero way to
            reach them: "3,214" Conversions rendered as "3,21…", Return on
            Spend didn't render at all. `flex-wrap` fixes this the same way
            already proven live elsewhere in this directory (CrmContactsFrame,
            DealPipelinePanel): once the row can't fit all 7 tiles on one
            line, it wraps to a second line instead of overflowing -- no
            tile ever shrinks below its own readable min-content width
            either way, wrap only changes WHERE an overflowing tile lands. */}
        <div className="flex flex-wrap rounded-lg border border-border bg-card divide-x divide-border">
          <StatBlock
            label="Ad spend"
            value={formatMoney(data.cost, data.currency)}
            context="this period"
            hero
            // Real period-over-period delta (data.comparison), not TrendBadge
            // off the raw daily cost_over_time -- see AdsSection.comparison's
            // doc comment: cost/impressions/clicks_over_time went daily,
            // which silently turned this into a day-over-day badge. The
            // sparkline below stays on cost_over_time -- it's a shape, not a
            // number, so the granularity change doesn't misrepresent it.
            badge={<ChangePctBadge pct={data.comparison?.cost_change_pct} metric="cost" />}
            sparkline={data.cost_over_time}
            periodEnd={periodEnd}
          />
          <StatBlock
            label="Impressions"
            value={formatNum(data.impressions)}
            badge={<ChangePctBadge pct={data.comparison?.impressions_change_pct} />}
          />
          <StatBlock
            label="Clicks"
            value={formatNum(data.clicks)}
            context={data.ctr != null ? `${formatPct(data.ctr)} CTR` : undefined}
            badge={<ChangePctBadge pct={data.comparison?.clicks_change_pct} />}
          />
          {data.search_impr_share != null && (
            <StatBlock
              label="Impr. share"
              value={formatPct(data.search_impr_share)}
              context="of available search auctions"
            />
          )}
          {data.avg_cpc != null && (
            <StatBlock
              label="Avg. CPC"
              // 2 decimals -- same sub-dollar-CPC fidelity concern as Meta's
              // equivalent tile (MetaAdsPanel.tsx).
              value={formatMoney(data.avg_cpc, data.currency, 2)}
              badge={<ChangePctBadge pct={data.comparison?.avg_cpc_change_pct} metric="avg_cpc" />}
            />
          )}
          <StatBlock
            label="Conversions"
            value={formatNum(data.conversions)}
            context={data.cost_per_conv != null ? `${formatMoney(data.cost_per_conv, data.currency)} / conv` : undefined}
          />
          {(() => {
            // ROAS from Google Ads = conversion value / spend. It only means
            // something when the account assigns dollar values to conversions.
            // Lead-gen clients rarely do, so a near-zero ROAS reads as failure
            // when it just means "value not tracked" — caveat that case rather
            // than show a misleading 0.0x.
            const hasConv = (data.conversions ?? 0) > 0;
            const valueTracked = (data.roas ?? 0) >= 0.5;
            if (valueTracked) {
              return (
                <StatBlock
                  label="Return on spend"
                  value={`${data.roas!.toFixed(1)}x`}
                  context="value per $ spent"
                />
              );
            }
            return (
              <StatBlock
                label="Return on spend"
                value={hasConv ? "n/a" : "—"}
                context={hasConv ? "conversion value not tracked" : "no conversion value"}
              />
            );
          })()}
        </div>
        {(data.search_rank_lost_impression_share != null ||
          data.search_budget_lost_impression_share != null) && (
          // Lost impression share, split by cause — AA's own "Google Ads
          // Opportunities" report renders this exact pair as two large
          // tiles side by side; matched here rather than folding them into
          // the KPI strip above, where a hero-sized number would compete
          // with "Ad spend". Account-level, impression-weighted across the
          // client's Search-only campaigns — see AdsSection's backend
          // docstring for why this isn't read verbatim from a single AA
          // field.
          //
          // flex-wrap for the same reason as the 7-tile KPI row above --
          // only 2 hero tiles today so overflow risk is low, but cheap
          // insurance against a future third Lost-IS cause and consistent
          // with every other KPI row on this dashboard.
          <div className="flex flex-wrap rounded-lg border border-border bg-card divide-x divide-border">
            <StatBlock
              label="Lost IS (rank)"
              value={formatPct(data.search_rank_lost_impression_share)}
              context="impression share lost to ad rank"
              hero
            />
            <StatBlock
              label="Lost IS (budget)"
              value={formatPct(data.search_budget_lost_impression_share)}
              context="impression share lost to budget"
              hero
            />
          </div>
        )}
        <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Spend trend
            </div>
            <div className="text-dash-context text-muted-foreground">daily</div>
          </div>
          <TrendSparkline points={data.cost_over_time} height={90} periodEnd={periodEnd} />
        </div>
        {(data.campaigns.length > 0 || data.ad_groups.length > 0 || data.keywords.length > 0) && (
          <AdEntityBreakdownTable
            campaigns={data.campaigns}
            adGroups={data.ad_groups}
            keywords={data.keywords}
            platform="Google Ads"
            currency={data.currency}
          />
        )}
      </div>
    </SectionPanel>
  );
}

// 6. PIPELINE — CRM deal-stage funnel + total deals/amount/closed/forecast/
// projected KPIs, plus a per-pipeline breakdown (GAP 2 fix, 2026-08-09).
// Shown only when the client has a connected CRM provider (data.crm
// present). `stages` stays ONE blended funnel across every pipeline
// (read_client_crm genuinely has no pipeline identifier) — `pipelines`
// below is the corrected, additive per-pipeline split sourced from the raw
// read_client_data_source route. See CrmSection's backend docstring for
// the full investigation.
export function PipelinePanel({ data }: { data: CrmSection }) {
  const stages: DealPipelineStage[] = data.stages.map((s) => ({ name: s.label, value: s.value }));
  return (
    <SectionPanel title="Pipeline" caption="Last 90 days · CRM" action={<SourceChip label="CRM" />}>
      <div className="p-5">
        <DealPipelinePanel
          closed={data.closed}
          forecast={data.forecast}
          projected={data.projected}
          currency={data.currency}
          stages={stages}
          amount={data.amount}
          totalDeals={data.total_deals}
          periodStart={data.period_start}
          periodEnd={data.period_end}
          comparison={data.comparison}
          pipelines={data.pipelines}
          pipelinesSampleSize={data.pipelines_sample_size}
        />
      </div>
    </SectionPanel>
  );
}

// 6b. CRM CONTACTS — HubSpot contacts KPI row + lifecycle-stage breakdown
// (GAP 1 of the contacts/multi-pipeline expansion). Shown only when the
// client's CRM connector returned a contacts pull (data.crm?.contacts).
// Separate panel from Pipeline (deals) rather than folded into it — AA's
// own report pages treat "HubSpot | Contacts" and "HubSpot Deals" as two
// distinct pages, and the two datasets have unrelated shapes (lifecycle
// stages vs. deal stages).
export function CrmContactsPanel({ data }: { data: CrmContactsSection }) {
  return (
    <SectionPanel title="Contacts" caption="Last 90 days · CRM" action={<SourceChip label="CRM" />}>
      <div className="p-5">
        <CrmContactsFrame contacts={data} />
      </div>
    </SectionPanel>
  );
}

// 6d. DEAL TABLE — the deal-level detail rows AA's "HubSpot Deals | MoM"
// report page ends on (reference screenshot: "Showing 50 of 289 Rows" +
// SOURCE filter). Sibling of Pipeline/Contacts, its own band below both.
// Adaptive: DealTable itself renders nothing for a zero-deal client (e.g.
// Precision Restorations) — caller doesn't need to gate on rows.length.
export function DealTablePanel({ data }: { data: CrmSection }) {
  if (!data.recent_deals || data.recent_deals.length === 0) return null;
  return (
    <SectionPanel title="Deals" caption="Last 90 days · CRM" action={<SourceChip label="CRM" />}>
      <div className="p-5">
        <DealTable
          rows={data.recent_deals}
          totalDeals={data.total_deals}
          currency={data.currency}
        />
      </div>
    </SectionPanel>
  );
}

// 6e. CONTACT TABLE — the raw per-contact detail rows AA's "HubSpot |
// Contacts" report page ends on (reference screenshot: "Showing 500 of
// 3,539 Rows" + search) and, filtered to a paid SOURCE, AA's "Google Ad
// Leads" view. Sibling of Contacts (lifecycle/country aggregates), its own
// band below it — same relationship DealTablePanel has to Pipeline.
// Adaptive: ContactTable itself renders nothing for a client with no raw
// contact-level rows — caller doesn't need to gate on rows.length.
export function ContactTablePanel({ data }: { data: CrmContactsSection }) {
  if (!data.recent_contacts || data.recent_contacts.length === 0) return null;
  return (
    <SectionPanel title="Contacts (raw)" caption="Last 90 days · CRM" action={<SourceChip label="CRM" />}>
      <div className="p-5">
        <ContactTable rows={data.recent_contacts} totalContacts={data.total} />
      </div>
    </SectionPanel>
  );
}

// 6c. HUBSPOT PAID ATTRIBUTION — AA "HubSpot | Paid Search" / "HubSpot |
// Paid Social" report page parity. Sessions/contacts attributed to paid
// channels, sourced from HubSpot via AA's channel-analytics AAQL asset.
// Shown only for the 3 clients with the hub-spot connector AND real
// paid-channel traffic (data.hubspot_attribution present) — ALIBI Music,
// Tyler McBroom, Precision Restorations as of 2026-08-09. Sibling of
// Pipeline/Contacts (also HubSpot-sourced) but a distinct dataset — paid
// session attribution, not CRM deals/lifecycle stages.
export function HubSpotAttributionPanel({
  data,
  periodEnd,
}: {
  data: HubSpotAttributionSection;
  // See LeadsPanel's periodEnd doc -- same partial-trailing-bucket guard.
  // Currently unused inside HubSpotAttributionFrame (its Sessions KPI
  // badge reads data.comparison now, not a TrendBadge off the daily
  // over_time series -- see HubSpotPeriodComparison's doc comment); kept
  // threaded through for interface consistency with every other panel in
  // this file, and in case a future trend-chart element needs it again.
  periodEnd?: string | null;
}) {
  return (
    <SectionPanel title="Paid Attribution" caption="Last 90 days · HubSpot" action={<SourceChip label="HubSpot" />}>
      <div className="p-5">
        <HubSpotAttributionFrame data={data} periodEnd={periodEnd} />
      </div>
    </SectionPanel>
  );
}

// 6f. EMAIL MARKETING — a top-level SIBLING of the Pipeline/Contacts/Deal
// Table/Contact Table/Paid Attribution CRM family above, deliberately NOT
// nested inside any `data.crm &&` conditional even though HubSpot is
// currently the only connected provider for both families. Krug's
// design-review verdict (2026-08-10): email is a marketing surface, not a
// sales/CRM relationship, so it gets its own top-level dashboard section —
// see EmailMarketingSection's backend doc comment for the full ruling.
// Placed here, immediately after the CRM family, purely for reading-order
// proximity (both surface via the same HubSpot connector today) — the
// nesting/gating is the thing that changed, not the page position.
//
// Adaptive, same contract as AdvertisingPanel/MetaAdsPanel above: a
// not-connected teaser when `data` is null/undefined (no email platform
// ever pulled for this client), or the real KPI strip even at a genuine
// zero (Precision Restorations: a connected, quiet HubSpot mailer that
// returns "No email campaign data found" for the window) — never the
// "connect" invite for an already-connected client. Campaign comparison
// cards (EmailMarketingFrame) render below the KPI strip only when there
// are real campaign rows to show.
// Provider slug -> display label, matching the backend's own
// _INSIGHT_PROVIDER_LABELS convention (mappers.py) for the same three
// read_client_email-supported slugs -- a generic title-case fallback would
// render "hub-spot" as "Hub Spot" (two words), not the brand's actual
// "HubSpot" casing.
const EMAIL_PROVIDER_LABELS: Record<string, string> = {
  "hub-spot": "HubSpot",
  "mail-chimp": "MailChimp",
  sendinblue: "Sendinblue",
};

export function EmailMarketingPanel({ data }: { data?: EmailMarketingSection | null }) {
  if (!data) {
    return (
      <NotConnected
        message="Email Marketing — no email platform is connected for this account"
        cta="Talk to your account manager"
      />
    );
  }
  const providerLabel = data.provider
    ? (EMAIL_PROVIDER_LABELS[data.provider] ?? data.provider.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()))
    : "Email Marketing";
  const openRatePct = formatRatePct(data.open_rate);
  const clickRatePct = formatRatePct(data.click_rate);
  const bounceRatePct = formatRatePct(data.bounce_rate);
  return (
    <SectionPanel
      title="Email Marketing"
      caption={`Last 90 days · ${providerLabel}`}
      action={<SourceChip label={providerLabel} />}
    >
      <div className="p-5 flex flex-col gap-5">
        {/* flex-wrap — same overflow-safety fix as every other multi-tile
            KPI row in this file (see AdvertisingPanel's own comment for the
            full mechanism this avoids). */}
        <div className="flex flex-wrap rounded-lg border border-border bg-card divide-x divide-border">
          <StatBlock label="Sent" value={formatNum(data.sent)} hero />
          <StatBlock label="Delivered" value={formatNum(data.delivered)} />
          <StatBlock
            label="Opens"
            value={formatNum(data.opens)}
            context={openRatePct ? `${openRatePct} open rate` : undefined}
          />
          <StatBlock
            label="Clicks"
            value={formatNum(data.clicks)}
            context={clickRatePct ? `${clickRatePct} click rate` : undefined}
          />
          <StatBlock
            label="Bounces"
            value={formatNum(data.bounces)}
            context={bounceRatePct ? `${bounceRatePct} bounce rate` : undefined}
          />
          <StatBlock label="Unsubscribes" value={formatNum(data.unsubscribes)} />
        </div>
        {data.campaigns.length > 0 ? (
          <EmailMarketingFrame campaigns={data.campaigns} />
        ) : (
          <div className="text-dash-context text-muted-foreground">No campaigns sent in this period.</div>
        )}
      </div>
    </SectionPanel>
  );
}

// 7. REVENUE — GA4 (or other connected storefront) ecommerce KPI row + trend.
// Shown only when the client has ecommerce tracking connected (data.ecommerce present).
export function RevenuePanel({ data }: { data: EcommerceSection }) {
  return (
    <SectionPanel title="Revenue" caption="Last 90 days · Ecommerce" action={<SourceChip label="Ecommerce" />}>
      <div className="p-5">
        <EcommerceRevenuePanel
          revenue={data.revenue}
          transactions={data.transactions}
          aov={data.aov ?? undefined}
          currency={data.currency}
          over_time={data.over_time}
        />
      </div>
    </SectionPanel>
  );
}

// 8. INSIGHTS — slim "what changed" strip of the biggest cross-provider
// movers, meant to sit near the top of the dashboard as a headline signal.
// Not wrapped in SectionPanel — this is a strip, not a card.
export function InsightsBar({ movers }: { movers: InsightMover[] }) {
  if (!movers || movers.length === 0) return null;
  const mapped: Mover[] = movers.map((m) => ({
    label: m.label,
    change_pct: m.change_pct,
    direction: m.direction,
    provider: m.provider ?? undefined,
  }));
  return (
    <div className="flex flex-col gap-2">
      <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
        What changed
      </div>
      <InsightsStrip movers={mapped} />
    </div>
  );
}

// 9a. SITE HEALTH — pairs with ContentPublishedPanel in a two-up row (assembler's call).
export function SiteHealthPanel({ data, linkTo = "#" }: { data: DashboardSiteHealthData; linkTo?: string }) {
  return (
    <SectionPanel
      title="Site Health"
      caption={data.crawl_date ? `Crawl ${data.crawl_date} · technical audit` : "Technical audit"}
      link={{ label: "View site health", to: linkTo }}
    >
      <div className="p-5 flex flex-col gap-2.5">
        <div className="flex gap-6">
          <div className="flex flex-col gap-0.5">
            <div className="text-dash-value font-bold tabular text-destructive">{formatNum(data.critical)}</div>
            <div className="text-dash-context text-muted-foreground">critical · being fixed</div>
          </div>
          <div className="flex flex-col gap-0.5">
            <div className="text-dash-value font-bold tabular text-[hsl(var(--status-warning))]">
              {formatNum(data.warnings)}
            </div>
            <div className="text-dash-context text-muted-foreground">warnings</div>
          </div>
          {data.pages_healthy_pct != null && (
            <div className="flex flex-col gap-0.5">
              <div className="text-dash-value font-bold tabular">{data.pages_healthy_pct}%</div>
              <div className="text-dash-context text-muted-foreground">pages healthy</div>
            </div>
          )}
        </div>
        <div className="text-dash-context text-muted-foreground">
          We monitor and fix these for you — no action needed.
        </div>
      </div>
    </SectionPanel>
  );
}

/* 9b. CONTENT PUBLISHED — two-up with SiteHealthPanel.

   Both panels in this row used to carry `h-full justify-center` on this
   content div. Card is a plain block, not a flex column, so `h-full` made
   the content box as tall as the WHOLE card while it starts below the
   title bar — taller than the space it occupies. Centring inside that
   oversized box pushed the numbers down past the real centre and over the
   bottom edge, which read on production as dead space under the title
   (reported 2026-10-01). Content sits at the top now, like every other
   panel in this file. The cards still stretch to equal height; only their
   contents stopped floating. */
export function ContentPublishedPanel({ data, linkTo = "#" }: { data: DashboardContentData; linkTo?: string }) {
  return (
    <SectionPanel title="Content Published" caption="Last 30 days" link={{ label: "View articles", to: linkTo }}>
      <div className="p-5 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="text-dash-value font-bold tabular">{formatNum(data.published_count)}</div>
          <div className="text-dash-context text-muted-foreground">new service articles on your site</div>
        </div>
        <div className="text-dash-context text-muted-foreground">
          Each targets a keyword your customers search for — see Search Performance above.
        </div>
      </div>
    </SectionPanel>
  );
}

// 10. WORK DONE — terminal accomplishment feed.
export function WorkDonePanel({ items }: { items: DashboardWorkDoneItem[] }) {
  return (
    <SectionPanel title="What we did this period" caption="Last 30 days · delivered by your Agile & Co. team">
      <div className="p-5 flex flex-col gap-3">
        {items.length === 0 ? (
          <div className="text-dash-context text-muted-foreground">No activity logged this period.</div>
        ) : (
          items.map((w, i) => (
            <div key={i} className="flex gap-2.5">
              <div className="mt-1.5 h-2 w-2 rounded-sm bg-[hsl(var(--status-success))] shrink-0" />
              <div className="flex flex-col gap-0.5">
                <div className="text-dash-subtitle">{w.text}</div>
                <div className="text-dash-context text-muted-foreground">{w.date}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </SectionPanel>
  );
}
